import React, { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar } from "lucide-react";
import PortableTextRenderer from "../components/PortableTextRenderer";
import BlogPageShell from "../components/BlogPageShell";
import { useLanguage } from "../context/LanguageContext";
import { useSanityQuery } from "../hooks/useSanityQuery";
import { sanityClient, isSanityConfigured } from "../lib/sanityClient";
import { getPostImageUrl } from "../lib/blogImages";
import { postBySlugQuery } from "../lib/queries";

const formatDate = (dateString, locale) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const locale = language === "uk" ? "uk-UA" : "en-CA";

  const { data: post, loading, error } = useSanityQuery(
    () => {
      if (!isSanityConfigured) return Promise.resolve(null);
      return sanityClient.fetch(postBySlugQuery, { slug, language });
    },
    [slug, language]
  );

  // Update URL when the translated post uses a different slug
  useEffect(() => {
    if (!loading && post?.slug && post.slug !== slug) {
      navigate(`/blog/${post.slug}`, { replace: true });
    }
  }, [post, slug, loading, navigate]);

  const imageUrl = post ? getPostImageUrl(post, { width: 1200, height: 630 }) : null;
  const metaDescription = post?.seoDescription || post?.excerpt || "";
  const notFound = !loading && !error && !post;

  return (
    <BlogPageShell>
      <div className="pt-28 pb-20">
        {post && (
          <Helmet>
            <title>
              {post.title} | {t("app.title")}
            </title>
            <meta name="description" content={metaDescription} />
            {imageUrl && <meta property="og:image" content={imageUrl} />}
            <meta property="og:title" content={post.title} />
            <meta property="og:description" content={metaDescription} />
          </Helmet>
        )}

        <div className="container mx-auto max-w-4xl px-6">
          <Link
            to="/blog"
            className="mb-10 inline-flex items-center gap-2 rounded-full border border-yellow-200/80 bg-white/70 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm backdrop-blur-sm transition hover:border-yellow-400 hover:text-yellow-700"
          >
            <ArrowLeft size={16} />
            {t("blog.backToBlog")}
          </Link>

          {!isSanityConfigured && (
            <div className="rounded-2xl border border-yellow-100/80 bg-white/80 p-12 text-center shadow-[0_8px_40px_rgba(161,98,7,0.08)] backdrop-blur-sm">
              <p className="text-lg text-gray-600">{t("blog.notConfigured")}</p>
            </div>
          )}

          {isSanityConfigured && loading && (
            <div className="py-20 text-center">
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-yellow-200 border-t-yellow-500" />
              <p className="mt-4 font-medium text-gray-500">{t("blog.loading")}</p>
            </div>
          )}

          {isSanityConfigured && (error || notFound) && (
            <div className="rounded-2xl border border-red-200 bg-red-50/80 p-12 text-center backdrop-blur-sm">
              <p className="text-red-600">{notFound ? t("blog.postNotFound") : t("blog.errorLoading")}</p>
              <Link to="/blog" className="mt-4 inline-block font-semibold text-yellow-600 hover:text-yellow-700">
                {t("blog.backToBlog")}
              </Link>
            </div>
          )}

          {isSanityConfigured && post && (
            <motion.article
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              {imageUrl && (
                <div className="relative mb-10 overflow-hidden rounded-2xl shadow-[0_20px_60px_rgba(161,98,7,0.15)]">
                  <img
                    src={imageUrl}
                    alt={post.mainImage?.alt || post.title}
                    className="h-64 w-full object-cover md:h-[28rem]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/30 to-transparent" />
                </div>
              )}

              <header className="mb-10">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-yellow-200/80 bg-white/70 px-4 py-1.5 text-sm font-medium text-yellow-800 shadow-sm backdrop-blur-sm">
                  <Calendar size={14} className="text-yellow-600" />
                  <time dateTime={post.publishedAt}>{formatDate(post.publishedAt, locale)}</time>
                </div>
                <h1 className="mb-5 text-4xl font-extrabold leading-tight tracking-tight text-gray-900 md:text-5xl">
                  {post.title}
                </h1>
                {post.excerpt && (
                  <p className="border-l-4 border-yellow-400 pl-5 text-xl leading-relaxed text-gray-600">
                    {post.excerpt}
                  </p>
                )}
              </header>

              <div className="rounded-2xl border border-white/80 bg-white/80 p-8 shadow-[0_8px_40px_rgba(161,98,7,0.08)] backdrop-blur-sm md:p-12">
                <PortableTextRenderer value={post.body} />
              </div>
            </motion.article>
          )}
        </div>
      </div>
    </BlogPageShell>
  );
};

export default BlogPost;
